import { CheckCircle2, Shield } from "lucide-react";
import { formatPriceCents } from "@/api/aimeansbusiness";

/**
 * PriceFramingCard - PHASE 5 Value Anchor Component
 * 
 * Ensures pricing is NEVER shown in isolation.
 * Always displays:
 * - Fixed-price language
 * - Scope includes
 * - Scope excludes
 * - Value statement
 * - Standardized cost-prevention callout
 */
const PriceFramingCard = ({ 
  serviceName,
  headline,
  priceCents,
  priceFormatted,  // Pre-formatted price string (e.g., from backend)
  scopeIncludes = [],
  scopeExcludes = [],
  whatThisPrevents = [],
  positioningStatement,
  showCompact = false,
  className = ""
}) => {
  // Use pre-formatted price if provided, otherwise format from cents
  const displayPrice = priceFormatted || (priceCents ? formatPriceCents(priceCents) : null);
  
  if (!displayPrice) {
    return null;
  }

  // Compact version for inline displays
  if (showCompact) {
    return (
      <div className={`bg-navy-950/60 rounded-xl p-5 border border-primary/20 ${className}`} data-testid="price-framing-compact">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-heading font-semibold text-white">{serviceName}</h4>
            {headline && <p className="text-slate-400 text-sm">{headline}</p>}
          </div>
          <div className="text-right">
            <p className="text-2xl font-heading font-bold text-primary">{displayPrice}</p>
            <p className="text-xs text-slate-500">Fixed Price</p>
          </div>
        </div>
        
        {/* Value Callout - Always Present */}
        <div className="mt-3 p-3 bg-green-950/30 border border-green-500/20 rounded-lg">
          <p className="text-green-300/90 text-xs leading-relaxed flex items-start gap-2">
            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>This engagement is designed to prevent far greater costs associated with acting without clarity.</span>
          </p>
        </div>
      </div>
    );
  }

  // Full version with all details
  return (
    <div className={`bg-navy-950/60 rounded-2xl border border-primary/20 overflow-hidden ${className}`} data-testid="price-framing-full">
      {/* Header with Price */}
      <div className="p-6 border-b border-slate-700/50">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-xl font-bold text-white mb-1">{serviceName}</h3>
            {headline && <p className="text-primary text-sm">{headline}</p>}
          </div>
          <div className="text-right">
            <p className="text-3xl font-heading font-bold text-primary">{displayPrice}</p>
            <p className="text-xs text-slate-400 uppercase tracking-wider">Fixed Price</p>
          </div>
        </div>
        
        {/* Positioning Statement */}
        {positioningStatement && (
          <p className="mt-4 text-slate-400 text-sm italic leading-relaxed">
            "{positioningStatement}"
          </p>
        )}
      </div>
      
      {/* What This Prevents - Risk Ledger (neutral tone, no marketing icons) */}
      {whatThisPrevents && whatThisPrevents.length > 0 && (
        <div className="p-6 bg-slate-900/50 border-b border-slate-700/50">
          <h4 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wide">
            What This Prevents
          </h4>
          <ul className="space-y-2">
            {whatThisPrevents.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3 text-sm">
                <span className="text-slate-500 mt-0.5">•</span>
                <span className="text-slate-400">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Scope Includes */}
      {scopeIncludes && scopeIncludes.length > 0 && (
        <div className="p-6 border-b border-slate-700/50">
          <h4 className="text-sm font-semibold text-slate-300 mb-3">What's Included:</h4>
          <ul className="space-y-2">
            {scopeIncludes.slice(0, 5).map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-slate-400">{item}</span>
              </li>
            ))}
            {scopeIncludes.length > 5 && (
              <li className="text-slate-500 text-xs pl-6">
                + {scopeIncludes.length - 5} more items included
              </li>
            )}
          </ul>
        </div>
      )}
      
      {/* Scope Excludes */}
      {scopeExcludes && scopeExcludes.length > 0 && (
        <div className="p-6 border-b border-slate-700/50 bg-slate-900/30">
          <h4 className="text-sm font-semibold text-slate-400 mb-3">Scope Boundaries:</h4>
          <ul className="space-y-1.5">
            {scopeExcludes.slice(0, 3).map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-xs">
                <span className="text-slate-500">•</span>
                <span className="text-slate-500">{item}</span>
              </li>
            ))}
            {scopeExcludes.length > 3 && (
              <li className="text-slate-600 text-xs pl-4">
                + {scopeExcludes.length - 3} additional boundaries
              </li>
            )}
          </ul>
        </div>
      )}
      
      {/* Value Anchor Callout - ALWAYS PRESENT */}
      <div className="p-6 bg-green-950/30">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h4 className="font-semibold text-green-300 text-sm mb-1">Value Investment</h4>
            <p className="text-green-300/80 text-sm leading-relaxed">
              This engagement is designed to prevent far greater costs associated with acting without clarity.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceFramingCard;
